import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { GROUPS, SORTS } from './todoNav';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A small panel that drops from a button - the page header's Display ▾ and
 * the phone's layout "List ▾". Opens on a click or Enter, takes the focus to
 * its first control, keeps Tab inside, and closes on Escape, a press
 * anywhere else or a choice (`close` is handed to the children), giving the
 * focus back to its button. Opens upwards when there is no room below, and
 * never past either edge of the window.
 */
export function Popover({ label, button, className = '', btnClass = '', title = null, children, align = 'right',
                          role = 'dialog' }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const wrap = useRef(null);
  const panel = useRef(null);
  const btn = useRef(null);
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus && btn.current) btn.current.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); close(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const els = [...panel.current.querySelectorAll(FOCUSABLE)].filter((x) => x.offsetParent !== null);
      if (!els.length) return;
      if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus(); }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
    // close() only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !panel.current || !btn.current) { setUp(false); return; }
    const r = btn.current.getBoundingClientRect();
    const h = panel.current.offsetHeight;
    setUp(r.bottom + h + 8 > window.innerHeight && r.top - h - 8 > 0);
    const first = panel.current.querySelector('[data-autofocus]') || panel.current.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });
  }, [open]);

  return (
    <div className={`tda-pop ${className}`} ref={wrap}>
      <button type="button" ref={btn} className={btnClass} aria-haspopup={role === 'menu' ? 'menu' : 'dialog'}
              aria-expanded={open} aria-label={label} title={title || label}
              onClick={() => setOpen((v) => !v)}>{button}</button>
      {open && (
        <div ref={panel} className={`tda-pop-panel is-${align}${up ? ' is-up' : ''}`} role={role} aria-label={label}>
          {children(close)}
        </div>
      )}
    </div>
  );
}

/**
 * THE DISPLAY MENU (page header): Show completed, Group by, Sort, Density
 * and - on Today - Show planned time. Only what means something on this
 * page is offered (`can`). On a phone its body sits inside the header's
 * one ⋯ (DisplayBody). (Where a to-do opens is no longer a choice: the
 * side panel is the one place, 26 Oct.) Every choice follows the person to
 * their other devices (GET/PUT /todos/view-prefs `display`).
 */
export function DisplayBody({ value, can, onChange }) {
  const set = (k) => (e) => onChange({ [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <div className="tda-display-body" data-display-menu="">
      {can.done && (
        <label className="tda-display-row is-check">
          <input type="checkbox" checked={Boolean(value.done)} onChange={set('done')} data-autofocus="" />
          <span>Show completed</span>
        </label>
      )}
      {can.planned && (
        <label className="tda-display-row is-check">
          <input type="checkbox" checked={value.planned !== false} onChange={set('planned')} />
          <span>Show planned time</span>
        </label>
      )}
      {can.group && (
        <label className="tda-display-row">
          <span>Group by</span>
          <select value={value.group || 'default'} onChange={set('group')} aria-label="Group by">
            {GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </label>
      )}
      {can.sort && (
        <label className="tda-display-row">
          <span>Sort by</span>
          <select value={value.sort || 'default'} onChange={set('sort')} aria-label="Sort by">
            {SORTS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </label>
      )}
      <fieldset className="tda-display-row is-radio">
        <legend>Density</legend>
        <span className="tda-seg" role="radiogroup" aria-label="Density">
          {[['comfortable', 'Comfortable'], ['compact', 'Compact']].map(([k, l]) => (
            <label key={k} className={`tda-seg-opt${(value.density || 'comfortable') === k ? ' is-on' : ''}`}>
              <input type="radio" name="tda-density" value={k} checked={(value.density || 'comfortable') === k}
                     onChange={set('density')} />
              <span>{l}</span>
            </label>
          ))}
        </span>
      </fieldset>
    </div>
  );
}

export function DisplayMenu({ value, can, onChange }) {
  return (
    <Popover label="Display options" className="tda-display" btnClass="tda-head-btn tda-display-btn"
             button={<><Icon name="sliders" size={15} /><span>Display</span><Icon name="chevronDown" size={12} /></>}>
      {() => <DisplayBody value={value} can={can} onChange={onChange} />}
    </Popover>
  );
}

/** List / Board / Calendar, as the header's segmented switch (or a compact menu on a phone). */
export const LAYOUTS = [
  { key: 'list', label: 'List', icon: 'list' },
  { key: 'board', label: 'Board', icon: 'board' },
  { key: 'calendar', label: 'Calendar', icon: 'calendarView' }
];
export function LayoutSwitch({ options, value, onChange, name, compact = false, disabled = false }) {
  const shown = LAYOUTS.filter((l) => options.includes(l.key));
  if (shown.length < 2) return null;
  if (compact) {
    const cur = shown.find((l) => l.key === value) || shown[0];
    return (
      <Popover label={`Show "${name}" as`} className="tda-layout-pop" btnClass="tda-head-btn tda-layout-btn" role="menu"
               button={<><Icon name={cur.icon} size={16} /><span className="tda-layout-name">{cur.label}</span><Icon name="chevronDown" size={12} /></>}>
        {(close) => (
          <div className="tda-layout-menu">
            {shown.map((l) => (
              <button key={l.key} type="button" role="menuitemradio" aria-checked={l.key === value} data-layout={l.key}
                      className={`tda-menu-item${l.key === value ? ' is-on' : ''}`} disabled={disabled}
                      onClick={() => { close(); onChange(l.key); }}>
                <Icon name={l.icon} /><span className="tda-menu-label">{l.label}</span>
                {l.key === value && <Icon name="check" size={14} />}
              </button>
            ))}
          </div>
        )}
      </Popover>
    );
  }
  return (
    <span className="tdb-switch" role="group" aria-label={`Show "${name}" as`}>
      {shown.map((l) => (
        <button key={l.key} type="button" aria-pressed={l.key === value} disabled={disabled} data-layout={l.key}
                title={`${l.label} (V then ${l.label[0]})`} onClick={() => onChange(l.key)}>
          <Icon name={l.icon} size={14} /><span>{l.label}</span>
        </button>
      ))}
    </span>
  );
}
